import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DeleteOutline as RemoveIcon,
  ChatOutlined as EvidenceIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Teléfonos vetados por la escalera anti-abuso del bot de captación (F2).
 * El veto es GLOBAL por teléfono: el bot ignora en silencio cualquier mensaje
 * de ese número. Esta pantalla lo hace reversible para el consultor
 * (endpoints /captation/vetoes ya existentes). Solo admin hacia arriba.
 */

// 569XXXXXXXX → +56 9 XXXX XXXX (solo presentación; si no calza, se muestra tal cual)
const displayPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('569')) {
    return `+56 9 ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }
  return phone || '';
};

// Timestamp de Firestore serializado ({_seconds}) o ISO → fecha legible
const displayDate = (ts) => {
  if (!ts) return '—';
  const date = typeof ts._seconds === 'number'
    ? new Date(ts._seconds * 1000)
    : new Date(ts);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const AtsCaptationVetoesTab = () => {
  const { t } = useTranslation();
  const { authToken, showAlert } = useStore.getState();

  const [vetoes, setVetoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // veto a quitar (dialog)
  const [evidenceTarget, setEvidenceTarget] = useState(null); // veto cuya conversación se muestra
  const [evidence, setEvidence] = useState(null); // conversación cargada
  const [loadingEvidence, setLoadingEvidence] = useState(false);

  const loadVetoes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/captation/vetoes', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data.response?.data || [];
      setVetoes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showAlert(t('ats.captationVetoes.loadError'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    loadVetoes();
  }, [loadVetoes]);

  // "Ver evidencia": el veto guarda el conversationId de la conversación
  // donde ocurrió la 2ª ofensa — el transcript incluye la advertencia del bot
  const handleShowEvidence = async (veto) => {
    setEvidenceTarget(veto);
    setEvidence(null);
    setLoadingEvidence(true);
    try {
      const res = await api.get(`/captation/conversations/${veto.conversationId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setEvidence(res.data.response?.data || null);
    } catch (err) {
      console.error(err);
      showAlert(t('ats.captationVetoes.evidenceLoadError'), 'error');
      setEvidenceTarget(null);
    } finally {
      setLoadingEvidence(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await api.delete(`/captation/vetoes/${removeTarget.phone}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      showAlert(t('ats.captationVetoes.removed'), 'success');
      setRemoveTarget(null);
      await loadVetoes();
    } catch (err) {
      console.error(err);
      showAlert(t('ats.captationVetoes.removeError'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {t('ats.captationVetoes.title')}
        </Typography>
        <Tooltip title={t('ats.captationVetoes.refresh')}>
          <IconButton onClick={loadVetoes} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.captationVetoes.description')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : vetoes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, fontStyle: 'italic' }}>
          {t('ats.captationVetoes.empty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('ats.captationVetoes.phone')}</TableCell>
              <TableCell>{t('ats.captationVetoes.reason')}</TableCell>
              <TableCell>{t('ats.captationVetoes.vetoedAt')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {vetoes.map((veto) => (
              <TableRow key={veto.id || veto.phone} hover>
                <TableCell sx={{ fontWeight: 600 }}>{displayPhone(veto.phone)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t(`ats.captationVetoes.reasons.${veto.reason}`, { defaultValue: veto.reason || '—' })}
                    sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>{displayDate(veto.vetoedAt)}</TableCell>
                <TableCell align="right">
                  {veto.conversationId && (
                    <Button
                      size="small"
                      startIcon={<EvidenceIcon />}
                      onClick={() => handleShowEvidence(veto)}
                      sx={{ fontSize: '0.7rem', mr: 1 }}
                    >
                      {t('ats.captationVetoes.evidence')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<RemoveIcon />}
                    onClick={() => setRemoveTarget(veto)}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t('ats.captationVetoes.remove')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Evidencia: transcript de la conversación del veto (mismo estilo
          de burbujas que el drawer del kanban) */}
      <Dialog
        open={Boolean(evidenceTarget)}
        onClose={() => setEvidenceTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('ats.captationVetoes.evidenceTitle', { phone: displayPhone(evidenceTarget?.phone) })}
        </DialogTitle>
        <DialogContent dividers>
          {loadingEvidence ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !evidence ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('ats.captationVetoes.evidenceEmpty')}
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ats.captationVetoes.evidenceMeta', {
                  channel: evidence.channel || '—',
                  date: displayDate(evidence.createdAt),
                })}
              </Typography>
              <Box
                sx={{
                  maxHeight: 400, overflowY: 'auto', p: 1.5, borderRadius: 2,
                  bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200',
                  display: 'flex', flexDirection: 'column', gap: 0.75,
                }}
              >
                {(evidence.transcript || []).map((m, i) => (
                  <Box
                    key={i}
                    sx={{
                      alignSelf: m.role === 'bot' ? 'flex-start' : 'flex-end',
                      maxWidth: '85%',
                      px: 1.5, py: 0.75, borderRadius: 2,
                      bgcolor: m.role === 'bot' ? 'white' : 'primary.main',
                      color: m.role === 'bot' ? 'text.primary' : 'white',
                      border: m.role === 'bot' ? '1px solid' : 'none',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                  </Box>
                ))}
                {(evidence.transcript || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('ats.captationVetoes.evidenceEmpty')}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceTarget(null)}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación MUI (regla: nunca window.confirm) */}
      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.captationVetoes.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ats.captationVetoes.removeConfirmText', { phone: displayPhone(removeTarget?.phone) })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            {t('cancel')}
          </Button>
          <Button onClick={handleRemove} color="error" variant="contained" disabled={removing}>
            {removing ? <CircularProgress size={18} /> : t('ats.captationVetoes.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtsCaptationVetoesTab;
